import { Box, Chip, TextField, useTheme } from '@mui/material';
import { RHFAutocomplete, RHFTextField } from 'src/components/minimals/form';
import { inputFiled } from '../../pages/style';
import { REPORT_TEMPLATE_DROPDOWN, SiteDropdownOption } from '../../config/pdfReportConfig';
import { ITimezoneOption } from 'react-timezone-select';
import TimeFrameDropdown from '../sidebar/form-inputs/TimeFrameDropdown';
import { TimeFrameRangePicker } from '../sidebar/form-inputs/TimeFrameRangePicker';
import { ReportType } from '../../types';
import { timeFrameOptions } from '../../utils/timeFrameToDateUtils';
import { useResponsive } from 'src/hooks/useResponsive';
import { useFormContext } from 'react-hook-form';

type props = {
  filteredCustomerSites: SiteDropdownOption[] | undefined;
  options: ITimezoneOption[];
  currentSite: any;
  layout: string;
  filteredAssets: { label: string; value: string; siteId: string }[];
  disabled?: boolean;
};
export const StatusReportForm = ({
  filteredCustomerSites,
  options,
  currentSite,
  layout,
  filteredAssets,
  disabled,
}: props) => {
  const REQUIRED_TEXT_FIELD = 'The field is required';
  const { watch, setValue, clearErrors } = useFormContext();
  const methods = useFormContext();
  const theme = useTheme();

  const isMobile = useResponsive('down', 'sm');

  const isTablet = useResponsive('down', 'lg');
  const isExtraLarge = useResponsive('down', 'xl');
  const assetChipCharLen = () => {
    if (isMobile) return 20;
    if (isTablet) return 30;
    if (isExtraLarge) return 15;
    return 40;
  };

  const chipLen = assetChipCharLen();
  const formErrors = methods.formState.errors?.data as Record<string, any> | undefined;

  return (
    <>
      <Box display="grid" gridTemplateColumns={layout} gap={3}>
        <TextField
          value="PDF"
          disabled={true}
          size="small"
          sx={{ width: '100%' }}
          label="Report Type"
        />
        <RHFAutocomplete
          freeSolo
          sx={{
            ...inputFiled,
            width: '100%',
          }}
          onChange={(_, data) => {
            setValue('header.reportTemplate', data);
          }}
          value={watch('header.reportTemplate')}
          name="header.reportTemplate"
          options={REPORT_TEMPLATE_DROPDOWN}
          rules={{ required: REQUIRED_TEXT_FIELD }}
          disableClearable
          disabled={disabled}
          size="small"
          renderOption={(props, option) => (
            <li
              {...props}
              key={option.value}
              style={{ backgroundColor: theme.palette.background.default }}
            >
              {option.label}
            </li>
          )}
          renderInput={(p) => (
            <TextField
              {...p}
              inputProps={{
                ...p.inputProps,
                'data-sm': 'report-template',
              }}
              fullWidth
              label={'Report Template *'}
            />
          )}
        />

        <RHFAutocomplete
          sx={{
            ...inputFiled,
            width: '100%',
          }}
          name="data.siteId"
          disabled={disabled}
          value={watch('data.siteId')}
          onChange={(_, newVal) => {
            setValue('data.assetIds', []);
            setValue('data.siteId', newVal);
            clearErrors('data.siteId');
          }}
          renderOption={(props, option) => (
            <li
              {...props}
              key={option.value}
              style={{ backgroundColor: theme.palette.background.default }}
            >
              {option.label}
            </li>
          )}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          options={filteredCustomerSites || []}
          rules={{ required: REQUIRED_TEXT_FIELD }}
          disableClearable
          size="small"
          renderInput={(p) => (
            <TextField
              {...p}
              inputProps={{
                ...p.inputProps,
                'data-sm': 'site',
              }}
              error={!!formErrors?.siteId}
              helperText={formErrors?.siteId?.message}
              fullWidth
              label={'Site *'}
            />
          )}
        />

        <RHFAutocomplete
          sx={{
            ...inputFiled,
            width: '100%',
          }}
          name="data.timeZone"
          rules={{ required: REQUIRED_TEXT_FIELD }}
          options={options}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          disableClearable
          disabled={disabled}
          value={watch('data.timeZone.value')}
          size="small"
          onChange={(_, newVal) => {
            setValue('data.timeZone', newVal);
            clearErrors('data.timeZone');
          }}
          renderOption={(props, option) => (
            <li
              {...props}
              key={option.value}
              style={{ backgroundColor: theme.palette.background.default }}
            >
              {option.label}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              error={!!formErrors?.timeZone}
              helperText={formErrors?.timeZone?.message}
              inputProps={{
                ...params.inputProps,
                'data-sm': 'time-zone',
              }}
              fullWidth
              label={'Time Zone *'}
            />
          )}
        />
        <TimeFrameDropdown
          disabled={disabled}
          value={watch('data.timeFrame')}
          onChange={(val: any) => setValue('data.timeFrame', val)}
          testId="asset-report-time-frame"
          timeFrames={timeFrameOptions}
        />
        {Array.isArray(watch('data.timeFrame')) && (
          <TimeFrameRangePicker
            disabled={disabled}
            value={watch('data.timeFrame')}
            isMobile={isMobile}
            onChange={(val) => setValue('data.timeFrame', val)}
            reportType={ReportType.PDF}
          />
        )}
        <RHFTextField
          label="Required data interval (s) *"
          disabled={disabled}
          name="data.dataInterval"
          testId="data-interval-field"
          rules={{
            required: REQUIRED_TEXT_FIELD,
            min: { value: 1, message: 'Value must be greater than 0' },
          }}
          onKeyDown={() => {
            clearErrors('data.dataInterval');
          }}
          size="small"
          type="number"
          InputProps={{
            inputProps: {
              min: 0,
              step: 0.1,
            },
          }}
        />
        <RHFTextField
          label="Outlier tolerance (%) *"
          disabled={disabled}
          name="data.outlierTolerance"
          testId="data-outlier-tolerance-field"
          rules={{
            required: REQUIRED_TEXT_FIELD,
            min: { value: 0, message: 'Value must be greater than 0' },
            max: { value: 100, message: 'Value must be less than 100' },
          }}
          onKeyDown={() => {
            clearErrors('data.outlierTolerance');
          }}
          size="small"
          type="number"
          InputProps={{
            inputProps: {
              min: 0,
              step: 0.1,
            },
          }}
        />
        <RHFTextField
          label="MLSS logs/week *"
          disabled={disabled}
          name="data.mlssPerWeek"
          type="number"
          InputProps={{
            inputProps: {
              min: 1,
              step: 1,
            },
          }}
          onKeyDown={(e) => {
            clearErrors('data.mlssPerWeek');
            if (e.key === '.' || e.key === ',' || e.key === 'e') {
              e.preventDefault();
            }
          }}
          rules={{
            required: REQUIRED_TEXT_FIELD,
            min: { value: 0, message: 'Value must be greater than or equal to 0' },
          }}
          testId="data-mlsslogs-field"
          size="small"
        />

        <RHFAutocomplete
          name="data.assetIds"
          multiple
          disabled={disabled || !currentSite}
          isOptionEqualToValue={(option, value) => option?.value === value?.value}
          options={filteredAssets || []}
          rules={{
            required: REQUIRED_TEXT_FIELD,
          }}
          defaultValue={[]}
          value={watch('data.assetIds') || []}
          onChange={(_, newVal) => {
            setValue('data.assetIds', newVal, {
              shouldValidate: true,
              shouldDirty: true,
            });
            clearErrors('data.assetIds');
          }}
          size="small"
          renderInput={(p) => (
            <TextField
              {...p}
              inputProps={{
                ...p.inputProps,
                'data-sm': 'assetdropdown-item',
              }}
              error={!!formErrors?.assetIds}
              fullWidth
              sx={{ maxHeight: '100%', display: 'flex', maxWidth: '100%' }}
              helperText={
                !currentSite ? 'Please select a site first' : formErrors?.assetIds?.message
              }
              label="Assets *"
            />
          )}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.value}
                label={
                  option.label.length > chipLen
                    ? option.label.slice(0, chipLen) + '...'
                    : option.label
                }
                size="small"
              />
            ))
          }
        />
      </Box>
    </>
  );
};
